import * as NavigationMenuPrimitive from '@radix-ui/react-navigation-menu'
import React from 'react'

import { styled } from '~/styled'
import { cn } from '~/styled'

import { colorSchemes as navigationMenuColorSchemes } from './NavigationMenu.colorscheme.config'
import { NavigationMenuContext } from './NavigationMenuContext'
import { NavigationMenuDropdown } from './NavigationMenuDropdown'
import { NavigationMenuDropdownContent } from './NavigationMenuDropdownContent'
import {
  NavigationMenuDropdownItem,
  NavigationMenuDropdownItemTitle
} from './NavigationMenuDropdownItem'
import { NavigationMenuDropdownTrigger } from './NavigationMenuDropdownTrigger'
import { NavigationMenuLink } from './NavigationMenuLink'

const StyledMenu = styled(NavigationMenuPrimitive.Root, {
  base: ['flex', 'justify-center', 'relative']
})

const StyledList = styled(NavigationMenuPrimitive.List, {
  base: ['flex', 'justify-center', 'items-center', 'gap-1', 'list-none']
})

const StyledViewport = styled(NavigationMenuPrimitive.Viewport, {
  base: ['z-2147483647']
})

const ViewportPosition = styled('div', {
  base: ['absolute', 'left-0', 'top-full', 'w-full', 'flex', 'justify-center']
})

const NavigationMenuComponent = ({
  children,
  className,
  ...props
}: React.ComponentProps<typeof StyledMenu>) => {
  const [offset, setOffset] = React.useState<number | null | undefined>()
  const [activeItem, setActiveItem] = React.useState<string | undefined>()
  const [listWidth, setListWidth] = React.useState(0)
  const listRef = React.useRef<HTMLUListElement>(null)

  React.useLayoutEffect(() => {
    if (listRef.current) {
      setListWidth(listRef.current.offsetWidth)
    }
  }, [React.Children.toArray(children).length])

  React.useEffect(() => {
    let timer: ReturnType<typeof setTimeout>

    // Delay transitioning back to initial position
    // to allow enough time for fadeOut animation to complete
    if (activeItem === '') {
      timer = setTimeout(() => setOffset(null), 200)
    }

    return () => {
      if (timer !== null) {
        clearTimeout(timer)
      }
    }
  }, [activeItem])

  // https://github.com/radix-ui/primitives/issues/1462
  const onNodeUpdate = (trigger: HTMLButtonElement, itemValue: string) => {
    if (trigger && listWidth && activeItem === itemValue) {
      const listCenter = listWidth / 2

      const triggerOffsetRight =
        listWidth -
        trigger.offsetLeft -
        trigger.offsetWidth +
        trigger.offsetWidth / 2

      setOffset(Math.round(listCenter - triggerOffsetRight))
    }

    return trigger
  }

  return (
    <NavigationMenuContext.Provider value={{ onNodeUpdate }}>
      <StyledMenu
        className={cn(className)}
        style={navigationMenuColorSchemes['light']}
        onValueChange={setActiveItem}
        {...props}
      >
        <StyledList ref={listRef}>{children}</StyledList>
        <ViewportPosition>
          <StyledViewport
            style={{ '--navigation-menu-viewport-offset': `${offset || 0}px` }}
            className={cn([
              'data-[state=open]:animate-fade-in',
              'data-[state=closed]:animate-fade-out',
              'translate-x-(--navigation-menu-viewport-offset)'
            ])}
          />
        </ViewportPosition>
      </StyledMenu>
    </NavigationMenuContext.Provider>
  )
}

export const NavigationMenu = Object.assign(NavigationMenuComponent, {
  Link: NavigationMenuLink,
  Dropdown: NavigationMenuDropdown,
  DropdownContent: NavigationMenuDropdownContent,
  DropdownItem: NavigationMenuDropdownItem,
  DropdownItemTitle: NavigationMenuDropdownItemTitle,
  DropdownTrigger: NavigationMenuDropdownTrigger
})

NavigationMenuComponent.displayName = 'NavigationMenu'
